import getCurrencySymbolFromCode from 'currency-symbol-map';
import { ProductType } from '@pages/product-analyzer/Product';
import { getSubscriptionDetailsByProductType } from '../AppSubscriptionUtils';
import { PagesConfig } from '../pageConfig/PagesConfigContext';
import { FactRevenue, FilterKeys, Filters, Option } from '../pageConfig/Types';
import { ColumnConfig } from '@components/design/migrated/Table/type';
import { Country } from '@models/Opa';
import { getAppliedCountry, getCountryListByCurrency } from '../CountryUtils';
import { uniqueObjectArrayByProp } from '../ObjectUtils';
import { byFilterKey } from '../pageConfig/Predicates/SelectedFiltersPredicates';
import { cloneDeep } from 'lodash';

/**
 * The details found in this list are taken from the Currency and Country tables in the database.
 */
export const DEFAULT_CURRENCY_DROPDOWN_COUNTRIES: Country[] = [
  {
    countryCode: 'US',
    countryName: 'United States of America',
    stCountryId: 39,
    currency: {
      currencyCode: 'USD',
      currencyName: 'United States of America, Dollars',
      stCurrencyId: 28,
    },
  },
  {
    countryCode: 'DE',
    countryName: 'Germany',
    stCountryId: 15,
    currency: {
      currencyCode: 'EUR',
      currencyName: 'Euro Member Countries, Euro',
      stCurrencyId: 1,
    },
  },
];

/**
 * Returns the currency code from the provided currency filter.
 *
 * The currency filter options are in the format of:
 * {
 *   label: "USD ($)",
 *   value: "USD ($)"
 * }
 * where both the label and value has the following form: <currencyCode> <currencySymbol>
 *
 * @param currencyFilter - The currency filter object.
 */
export const getCurrencyCode = (currencyFilter: Filters) => currencyFilter.options[0].value.substring(0, 3);

/**
 * Finds the country from the default currency dropdown countries list based on the given currency
 * filter.
 *
 * @param currencyFilter - The currency filter to use for finding the currency.
 */
export const findCountryFromDefaultCurrenciesByCurrency = (currencyFilter: Filters) =>
  DEFAULT_CURRENCY_DROPDOWN_COUNTRIES.find(
    (country) => country.currency.currencyCode === getCurrencyCode(currencyFilter),
  );

export const findCountryFromDefaultCurrenciesByCountryOption = (countryOption: Option): Country => {
  const country = DEFAULT_CURRENCY_DROPDOWN_COUNTRIES.find(
    (defaultCountry) => defaultCountry.countryCode === countryOption.value,
  );

  // Default to first country if not found
  return country ?? DEFAULT_CURRENCY_DROPDOWN_COUNTRIES[0];
};

export const constructFactRevenue = (currencySymbol: string = '', currencyLabel?: string): FactRevenue => {
  return {
    currencySymbol: currencySymbol,
    currencyLabel: currencyLabel === 'LC' ? '' : currencyLabel,
  };
};

// used to get the currencySymbol and currencyLabel from subscription
export const getFactRevenue = (
  pagesConfig: PagesConfig,
  selectedCountries: Option[],
  productType: ProductType,
): FactRevenue => {
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;
  // NEO-24170: For export, perhaps need to create separate function to get currency from appliedFilters
  if (selectedCountries.length !== 0) {
    const selectedCountry = currentClientCountries.find((country) => country.id === selectedCountries[0].value);
    if (selectedCountry) {
      return constructFactRevenue(getCurrencySymbolFromCode(selectedCountry?.currency), selectedCountry.currencyLabel);
    }
  }
  return constructFactRevenue();
};

/**
 * Returns the currencySymbol and currencyLabel from subscription and applied countries
 * Label will be based on the applied country
 * Currency symbol is based on the currency country
 * NEO-27517
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param currencyCountry - The country of the selected currency.
 * @param productType - The current active product type.
 */
export const getFactRevenueWithCurSymbolAndLabel = (
  pagesConfig: PagesConfig,
  currencyCountry: Option,
  productType: ProductType,
): FactRevenue => {
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;
  const selectedCountries = getAppliedCountry(pagesConfig);

  if (selectedCountries.length !== 0) {
    const selectedCountry = currentClientCountries.find((country) => country.id === selectedCountries[0].value);
    const curSymbolCountry = currentClientCountries.find((country) => country.id === currencyCountry.value);

    if (selectedCountry && curSymbolCountry) {
      if (selectedCountry.id === curSymbolCountry.id) {
        return constructFactRevenue(
          getCurrencySymbolFromCode(curSymbolCountry.currency),
          curSymbolCountry.currencyLabel,
        );
      }
      return constructFactRevenue(getCurrencySymbolFromCode(curSymbolCountry.currency), '');
    }
  }
  return constructFactRevenue();
};

/**
 * Returns details of a currency's symbol and label based on the applied currency filter.
 *
 * It first checks if the selected currency is either USD or EUR before checking against the
 * subscription's client countries.
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param appliedCountries - A reference to applied countries in side filter.
 * @param productType - The current active product type.
 */
export const getFactRevenueBySelectedCurrency = (
  pagesConfig: PagesConfig,
  appliedCountries: Option[],
  productType: ProductType,
): FactRevenue => {
  const currencyFilter = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters.find(
    byFilterKey(FilterKeys.CURRENCY),
  );

  if (!currencyFilter) {
    return constructFactRevenue();
  }

  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;

  //This is to cater for single applied country
  if (appliedCountries.length === 1) {
    const appliedClientCountry = currentClientCountries.find(
      (country) => country.id === appliedCountries[0].value && country.currency === getCurrencyCode(currencyFilter),
    );
    if (appliedClientCountry) {
      return constructFactRevenue(
        getCurrencySymbolFromCode(appliedClientCountry?.currency),
        appliedClientCountry.currencyLabel,
      );
    }
  }
  // Search if the selected currency is either USD or EUR
  const defaultCountry = findCountryFromDefaultCurrenciesByCurrency(currencyFilter);
  if (defaultCountry) {
    const appliedClientCountry = currentClientCountries.find(
        (country) => country.id === appliedCountries[0].value
    );
    const currencyLabel = appliedClientCountry?.currencyLabel ?? defaultCountry.currency.currencyLabel;
    return constructFactRevenue(
        getCurrencySymbolFromCode(defaultCountry.currency.currencyCode),
        currencyLabel,
    );
  }

  // Search if the selected currency is in the subscription's client countries
  return getFactRevenueWithCurSymbolAndLabel(
    pagesConfig,
    getCountryListByCurrency(pagesConfig, productType, currencyFilter)[0],
    productType,
  );
};

/**
 * Converts a country object into an option object to be used as a filter option.
 *
 * @param country - The country object to be converted.
 */
export const getCurrencyOption = (country: Country): Option => {
  const { currencyCode } = country.currency;
  const currencySymbol = getCurrencySymbolFromCode(currencyCode)!;
  const currencyLabel = `${currencyCode} (${currencySymbol})`;

  return {
    label: currencyLabel,
    value: currencyLabel,
  };
};

/**
 * Returns a list of currency options from the subscription stored within pagesConfig based on the
 * current active client used.
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param productType - The current active product type.
 * @param currentClientId - The current active client id.
 */
export const getCurrencyOptionsFromSubscription = (
  pagesConfig: PagesConfig,
  productType: ProductType,
  currentClientId: number,
): Option[] => {
  const { currentSubscription, currentClientCountries } = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const countryIds: string[] = currentClientCountries.map((country) => country.id);

  return uniqueObjectArrayByProp(
    [
      ...currentSubscription.result.restrictions
        .filter(({ retailer }) => retailer === currentClientId)
        .filter(({ country }) => countryIds.includes(country))
        .map(({ countryObject }) => getCurrencyOption(countryObject)),
      ...[
        getCurrencyOption(DEFAULT_CURRENCY_DROPDOWN_COUNTRIES[0]),
        getCurrencyOption(DEFAULT_CURRENCY_DROPDOWN_COUNTRIES[1]),
      ],
    ],
    'value', // represents the currency code
  );
};

/**
 * Determines if the currency filter passed is the current applied country's default currency.
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param productType - The current active product type.
 * @param currencyFilter - The currency filter to check.
 * @param appliedCountry - The current applied country filter.
 */
export const isLocalCurrencyApplied = (
  pagesConfig: PagesConfig,
  productType: ProductType,
  currencyFilter: Filters,
  appliedCountry: Filters,
) => {
  if (appliedCountry.options.length > 1) {
    return false;
  }
  const countryListByCurrency = getCountryListByCurrency(pagesConfig, productType, currencyFilter);
  return countryListByCurrency.map((country) => country.value).includes(appliedCountry.options[0].value);
};

// used to generate title for Revenue/Revenue (currencyLabel)
export const constructRevenueTitle = (factRevenue: FactRevenue): string => {
  return factRevenue.currencyLabel ? `Revenue (${factRevenue.currencyLabel})` : 'Revenue';
};

// used to generate the currency label within the brackets
export const constructCurrencyLabel = (factRevenue: FactRevenue): string => {
  return factRevenue.currencyLabel ? ` (${factRevenue.currencyLabel})` : '';
};

// used to generate the currency symbol within the brackets
export const constructCurrencySymbol = (factRevenue: FactRevenue): string => {
  return factRevenue.currencySymbol ? ` (${factRevenue.currencySymbol})` : '';
};

// used to format the label into the title behind the word 'Revenue' or 'Price'
export const formatLabelIntoRevenuePriceTitle = (title: string, label: string) => {
  if (!title) {
    return title;
  }
  if (title.includes('Price')) {
    return title.replace('Price', `Price${label}`);
  }
  return title.replace('Revenue', `Revenue${label}`);
};

// used to check if the headerKey is of revenue type that needs to display the currency label or not
export const isRevenueType = (headerKey: string): boolean => {
  const revenueList = [
    'marketRevenue',
    'yourRevenue',
    'yourRevenueShare',
    'yourRevenueMix',
    'marketPrice',
    'yourPrice',
    'myRevenue',
    'myShareRevenue',
    'growthOppRevenue',
    'fairShareOppRevenue',
    'marketExcludeRetailRevenue',
    'marketExcludeRetailPrice',
  ];
  return revenueList.includes(headerKey);
};

// used to format the header based on a single ColumnConfig
export const formatCurrencyLabelColumnConfig = (
  columnConfig: ColumnConfig,
  factRevenue: FactRevenue,
): string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Element => {
  if (isRevenueType(columnConfig.headerKey)) {
    return formatLabelIntoRevenuePriceTitle(columnConfig.name.toString(), constructCurrencyLabel(factRevenue));
  }
  return columnConfig.name;
};

// used to format the headers in a list of ColumnConfig (Tables)
export const formatCurrencyLabelColumnConfigList = (
  columnConfig: ColumnConfig[],
  factRevenue: FactRevenue,
): ColumnConfig[] => {
  columnConfig.forEach((column) => {
    column.name = formatCurrencyLabelColumnConfig(column, factRevenue);
  });
  return columnConfig;
};

export const getExchangeRate = (pagesConfig: PagesConfig, filters?: Filters[], country?: string) => {
  const rate = pagesConfig.currencyExchangeRatePerCountry;
  const selectedFilters: Filters[] = filters ??  cloneDeep(pagesConfig.selectedFilters[pagesConfig.pageType].selectedFilters);
  const selectedCountry: string = country ?? selectedFilters.find((f) => f.filterKey === FilterKeys.COUNTRY)?.options[0]?.value ?? '';
  const selectedCurrency: string = selectedFilters
    .find((f) => f.filterKey === FilterKeys.CURRENCY)
    ?.options[0]?.value.split(' ')[0] ?? '';

  if (!selectedCountry || !selectedCurrency) return -1;

  const exRateObj = rate?.find((f) => f.country === selectedCountry);
  return exRateObj ? exRateObj.currencyExchangeRate[selectedCurrency] : -1;
};
